
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { FeatureType, RecentScan } from '@/src/types';

interface RecentScansProps {
  scans: RecentScan[];
  onRecentClick: (scan: RecentScan) => void;
  subscriptionTier: string;
  onUpgradePrompt: () => void;
  onOpenSavedOpportunities?: () => void;
  savedCount?: number;
}

type ScanFilter = 'all' | 'launchpad' | 'discovery';

const timeAgo = (timestamp: number) => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(timestamp).toLocaleDateString();
};

const RecentScans: React.FC<RecentScansProps> = ({ 
  scans, 
  onRecentClick, 
  subscriptionTier, 
  onUpgradePrompt,
  onOpenSavedOpportunities,
  savedCount = 0
}) => {
  const [activeFilter, setActiveFilter] = useState<ScanFilter>('all');

  if (scans.length === 0) return null;

  const isPremium = subscriptionTier === 'premium' || subscriptionTier === 'premium_pro';

  const categorizedScans = useMemo(() => {
    const seenIds = new Set<string>();
    return scans.map((scan) => {
      const isLaunchpad = [
        FeatureType.Scan, 
        FeatureType.GapFinder, 
        FeatureType.LaunchMath, 
        FeatureType.LocaleLaunchpad,
        FeatureType.Optimize
      ].includes(scan.type);
      
      const isDiscovery = [
        FeatureType.ReverseDiscovery, 
        FeatureType.DensityScout,
        FeatureType.GrassrootsIdeator
      ].includes(scan.type);

      let scanId = scan.id;
      if (!scanId || typeof scanId !== 'string' || !scanId.trim()) {
        scanId = `scan-${scan.timestamp || Date.now()}-${(scan.location || '').replace(/\s+/g, '-')}-${(scan.sector || '').replace(/\s+/g, '-')}`;
      }

      let finalId = scanId;
      let counter = 1;
      while (seenIds.has(finalId)) {
        finalId = `${scanId}-${counter}`;
        counter++;
      }
      seenIds.add(finalId);

      return {
        ...scan,
        id: finalId,
        hub: isLaunchpad ? 'launchpad' : (isDiscovery ? 'discovery' : 'other')
      };
    });
  }, [scans]);

  const filteredScans = useMemo(() => {
    const baseList = activeFilter === 'all' 
      ? categorizedScans 
      : categorizedScans.filter(s => s.hub === activeFilter);
    
    return isPremium ? baseList : baseList.slice(0, 3);
  }, [categorizedScans, activeFilter, isPremium]);

  const hasMore = scans.length > 3 && !isPremium;

  return (
    <div className="pt-12 text-center">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex flex-col items-center mb-10"
      >
        <h4 className="text-xs uppercase tracking-[0.2em] text-white/60 font-medium mb-6">
          Recent Reconnaissance
        </h4>
        
        <div className="flex flex-wrap items-center justify-center gap-3">
          <div className="flex bg-[#3a3430] p-1 rounded-xl border border-white/[0.06]">
            {[
              { id: 'all', label: 'All' },
              { id: 'launchpad', label: 'Launchpad' },
              { id: 'discovery', label: 'Discovery' }
            ].map((filter) => (
              <button
                key={`scan-filter-${filter.id}`}
                onClick={() => setActiveFilter(filter.id as ScanFilter)}
                className={`px-5 py-2 text-[10px] font-medium uppercase tracking-widest transition-all rounded-lg cursor-pointer ${
                  activeFilter === filter.id 
                    ? 'bg-[#322D29] text-white border border-white/[0.08]' 
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {onOpenSavedOpportunities && (
            <button
              onClick={onOpenSavedOpportunities}
              id="recent-scans-access-saved-btn"
              className="flex items-center gap-2 px-4 py-2 bg-[#3a3430] hover:bg-white/10 border border-white/[0.06] hover:border-[#F26522]/40 rounded-xl text-white/80 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-all cursor-pointer font-heading group"
            >
              <i className="fas fa-bookmark text-xs text-[#F26522] transition-transform group-hover:scale-110"></i>
              <span>Access Saved Opportunities</span>
              {savedCount > 0 && (
                <span className="bg-[#F26522] text-white text-[9px] font-bold px-1.5 py-0.2 rounded-full leading-none">
                  {savedCount}
                </span>
              )}
            </button>
          )}
        </div>
      </motion.div>

      <div className="flex flex-wrap justify-center gap-4">
        <AnimatePresence mode="popLayout">
          {filteredScans.map((scan, i) => {
            if (!scan) return null;
            return (
              <motion.button 
                key={`recent-scan-${scan.id || `item-${i}`}-${i}`} 
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                layout
                onClick={() => onRecentClick(scan)} 
                className="group relative flex flex-col items-start px-6 py-4 bg-[#3a3430] border border-white/[0.06] hover:border-[#F26522]/50 transition-all rounded-xl min-w-[200px] cursor-pointer text-left"
              >
                <div className="flex items-center justify-between w-full mb-2">
                  <span className={`text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                    scan.hub === 'launchpad' 
                      ? 'bg-[#F26522]/10 text-[#F26522] border-[#F26522]/20' 
                      : 'bg-white/5 text-white/80 border-white/10'
                  }`}>
                    {scan.hub === 'launchpad' ? 'Launchpad' : scan.hub}
                  </span>
                  <span className="text-[9px] text-white/40 group-hover:text-white/60">
                    {timeAgo(scan.timestamp)}
                  </span>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg border border-white/[0.06] flex items-center justify-center bg-[#322D29] group-hover:bg-[#F26522] transition-colors`}>
                    <i className={`fas ${scan.hub === 'launchpad' ? 'fa-rocket text-[#F26522]' : 'fa-compass text-white/70'} text-xs group-hover:text-white transition-colors`}></i>
                  </div>
                  <div className="flex flex-col items-start overflow-hidden">
                    <span className="text-xs font-medium text-white truncate max-w-[140px]">
                      {scan.location}
                    </span>
                    {scan.sector && (
                      <span className="text-[10px] text-white/50 truncate max-w-[140px]">
                        {scan.sector}
                      </span>
                    )}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </AnimatePresence>
        
        {hasMore && filteredScans.length > 0 && (
          <motion.button
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={onUpgradePrompt}
            className="flex flex-col items-center justify-center px-6 py-4 bg-[#3a3430] border border-dashed border-white/20 text-white/60 hover:border-[#F26522] hover:text-[#F26522] transition-all rounded-xl min-w-[120px] group cursor-pointer"
          >
            <i className="fas fa-plus text-[10px] mb-2 group-hover:scale-110 transition-transform"></i>
            <span className="text-[8px] font-bold uppercase tracking-widest">More Results</span>
          </motion.button>
        )}
      </div>

      {filteredScans.length === 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-12"
        >
          <div className="w-12 h-12 rounded-xl border border-white/[0.06] flex items-center justify-center bg-[#3a3430] mb-4 opacity-40">
            <i className="fas fa-ghost text-white"></i>
          </div>
          <p className="text-xs text-white/40 uppercase tracking-widest">
            No reconnaissance recorded for this operational sector
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default RecentScans;

